/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import profilePhoto from "../../assets/profile_photo.svg";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";

function Banners() {
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingBannerId, setDeletingBannerId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBannerId, setSelectedBannerId] = useState(null);
  const [selectedBannerText, setSelectedBannerText] = useState("");

  const { listOfAllBanners, deleteBanner } = useContext(APIContext);

  // Helper function to check if banner is effectively published
  const isBannerPublished = (banner) => {
    // If published field is false, it's not published
    if (!banner.published) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    // Check if start date is in the future
    if (banner.start_date) {
      const startDate = typeof banner.start_date === 'number' 
        ? banner.start_date 
        : parseInt(banner.start_date);
      if (startDate > now) {
        return false; // Banner hasn't started yet
      }
    }

    // Check if expiry date is in the past
    if (banner.expiry_date) {
      const expiryDate = typeof banner.expiry_date === 'number' 
        ? banner.expiry_date 
        : parseInt(banner.expiry_date);
      if (expiryDate < now) {
        return false; // Banner has expired
      }
    }

    return true; // Banner is published and within date range
  };

  useEffect(() => {
    listOfAllBanners()
      .then((banners) => {
        setBannerList(banners || []);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error.message);
        toast.error("Failed to load banners", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setLoading(false);
      });
  }, []);

  const handleDeleteClick = (bannerId, bannerText) => {
    setSelectedBannerId(bannerId);
    setSelectedBannerText(bannerText || "Untitled Banner");
    onOpen();
  };

  const handleDeleteConfirm = () => {
    if (!selectedBannerId || deletingBannerId) {
      return;
    }

    setDeletingBannerId(selectedBannerId);
    deleteBanner(selectedBannerId)
      .then((res) => {
        setDeletingBannerId(null);
        toast.success("Banner deleted!", {
          position: toast.POSITION.TOP_RIGHT,
        });
        onClose();
        // Refresh the list
        listOfAllBanners()
          .then((banners) => {
            setBannerList(banners || []);
          })
          .catch((error) => {
            console.log(error.message);
          });
      })
      .catch((error) => {
        console.error(error);
        setDeletingBannerId(null);
        toast.error("Failed to delete banner", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  return (
    <>
      <HelmetLayout title="Banners" />
      {!loading && (
        <div className="font-Inter flex-1 h-screen overflow-y-scroll bg-white items-start p-7">
          <div className="flex flex-col items-center justify-start">
            <div className="w-full flex flex-row justify-between mb-6">
              <h1 className="text-[32px]">Banners</h1>
              <img
                src={profilePhoto}
                className="mr-4 h-9"
                alt="user-profile-img"
              />
            </div>

            {/* Card Section */}
            <div className="w-full flex justify-start items-center m-5 ml-0 gap-[14px]">
              <div className="rounded-[29px] shadow-card-box-shadow">
                <div className="w-[255px] h-[100px] flex flex-col justify-center items-center">
                  <div className="flex flex-col items-start">
                    <h1 className="font-bold">{bannerList.length}</h1>
                    <p>Total Banners</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Add New Banner Button */}
            <div className="w-full flex justify-between items-center">
              <h1 className="text-[#121213] text-2xl">All Banners</h1>
              <div className="flex gap-[10px]">
                <Link to="/banners/add">
                  <button className="rounded-[20px] bg-[#F5F7FC] px-4 py-3 items-center text-[14px] text-center">
                    Add New
                  </button>
                </Link>
              </div>
            </div>

            {/* Banners List Section */}
            <div className="w-full flex flex-col justify-start items-center gap-5 mt-10">
              {bannerList.length === 0 ? (
                <div className="text-gray-500 text-lg">No banners found</div>
              ) : (
                bannerList.map((banner) => {
                  const bannerID = banner.banner_id || (banner.pk ? banner.pk.replace("banner-", "") : null);
                  if (!bannerID) return null;
                  return (
                    <div
                      key={bannerID}
                      className="w-full rounded-[9px] shadow-card-box-shadow p-5 bg-white"
                    >
                      <div className="flex flex-row justify-between items-center">
                        <Link to={`/banners/${bannerID}`} className="flex-1">
                          <div className="flex flex-row justify-between items-center">
                            <div className="flex flex-col gap-1">
                              <h2 className="text-xl font-semibold">
                                {banner.banner_name || "Untitled Banner"}
                              </h2>
                              <div className="flex items-center gap-2">
                                {(() => {
                                  const isPublished = isBannerPublished(banner);
                                  return (
                                    <span
                                      className={`px-2 py-1 rounded text-xs font-medium ${
                                        isPublished
                                          ? "bg-green-100 text-green-700"
                                          : "bg-gray-100 text-gray-700"
                                      }`}
                                    >
                                      {isPublished ? "Published" : "Unpublished"}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                            <div
                              className="px-4 py-2 rounded-lg text-white text-sm"
                              style={{ backgroundColor: banner.banner_color || "#5CBD20" }}
                            >
                              Preview
                            </div>
                          </div>
                        </Link>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDeleteClick(bannerID, banner.banner_name);
                          }}
                          className="ml-4 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete banner"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div className="flex justify-center items-center h-screen">
          <PulseLoader size={15} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        blockScrollOnMount={false}
        isOpen={isOpen}
        onClose={onClose}
        borderRadius="20px"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader
            mt="20px"
            textAlign="center"
            fontSize="24px"
            fontWeight="medium"
          >
            Delete Banner
          </ModalHeader>
          <ModalBody>
            <Text
              color="#121213"
              textAlign="center"
              fontWeight="normal"
              fontSize="16px"
              mb="1rem"
            >
              Are you sure you want to delete "{selectedBannerText}"? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Box width="100%" display="flex" justifyContent="center" gap="10px">
              <Box
                onClick={onClose}
                as="button"
                width="120px"
                height="48px"
                lineHeight="1.2"
                px="8px"
                borderRadius="20px"
                fontSize="16px"
                fontWeight="normal"
                bg="#F5F7FC"
                color="#121213"
                mb="30px"
                _hover={{
                  bg: "#E5E7EB",
                }}
                _focus={{
                  boxShadow:
                    "0px 70.8569px 51.2194px rgba(41, 72, 152, 0.037963), 0px 42.1092px 27.8569px rgba(41, 72, 152, 0.0303704), 0px 21.8644px 14.2119px rgba(41, 72, 152, 0.025)",
                }}
              >
                Cancel
              </Box>
              <Box
                onClick={handleDeleteConfirm}
                as="button"
                width="120px"
                height="48px"
                lineHeight="1.2"
                px="8px"
                borderRadius="20px"
                fontSize="16px"
                fontWeight="normal"
                bg="#DE1111"
                color="#ffffff"
                mb="30px"
                _hover={{
                  bg: "#B91C1C",
                }}
                _focus={{
                  boxShadow:
                    "0px 70.8569px 51.2194px rgba(41, 72, 152, 0.037963), 0px 42.1092px 27.8569px rgba(41, 72, 152, 0.0303704), 0px 21.8644px 14.2119px rgba(41, 72, 152, 0.025)",
                }}
              >
                {deletingBannerId === selectedBannerId ? (
                  <PulseLoader size={8} color="#ffffff" />
                ) : (
                  "Delete"
                )}
              </Box>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Banners;

